import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFamily } from '../contexts/FamilyContext';
import { api } from '../App';
import { toast } from 'sonner';
import { Users, ArrowRight, Shield, Sparkles, CheckCircle } from 'lucide-react';
import Logo from '../components/Logo';

const FamilySetup = () => {
  const navigate = useNavigate();
  const { code: urlCode } = useParams(); // Get invite code from URL if present
  const { createFamily, acceptInvite, needsFamilySetup } = useFamily();
  const [familyName, setFamilyName] = useState('');
  const [inviteCode, setInviteCode] = useState(urlCode || '');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(urlCode ? 'join' : 'create'); // 'create' or 'join'
  const [inviteDetails, setInviteDetails] = useState(null);

  // If there's a URL code, look up the invite details
  useEffect(() => {
    if (urlCode) {
      lookupInvite(urlCode);
    }
  }, [urlCode]);

  const lookupInvite = async (code) => {
    try {
      const res = await api.get(`/invites/lookup/${code.toUpperCase()}`);
      setInviteDetails(res.data);
    } catch (error) {
      toast.error('Invalid or expired invite link');
      setMode('create');
    }
  };

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    if (!familyName.trim()) {
      toast.error('Please enter a family/group name');
      return;
    }

    setLoading(true);
    try {
      await createFamily(familyName.trim());
      toast.success(`Welcome to ${familyName}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create family');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinFamily = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setLoading(true);
    try {
      const result = await acceptInvite(inviteCode.trim().toUpperCase());
      toast.success(result.message);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired invite code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <Logo size="large" className="justify-center mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Welcome to Maa Dabbu</h1>
          <p className="text-gray-600 mt-2">Let's set up your family wealth tracker</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setMode('create')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                mode === 'create'
                  ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Users className="inline-block mr-2" size={18} />
              Create Family
            </button>
            <button
              onClick={() => setMode('join')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                mode === 'join'
                  ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <ArrowRight className="inline-block mr-2" size={18} />
              Join Existing
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {mode === 'create' ? (
              <form onSubmit={handleCreateFamily} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Family / Group Name
                  </label>
                  <input
                    type="text"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    placeholder="e.g., The Sharmas, My Portfolio"
                    className="input-field w-full text-lg"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    This is how your family or investment group will be identified
                  </p>
                </div>

                {/* Features Preview */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">You'll get access to:</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Sparkles size={14} className="text-amber-500" />
                      Portfolio Tracking
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Sparkles size={14} className="text-amber-500" />
                      Debt Management
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Sparkles size={14} className="text-amber-500" />
                      AI Analysis
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Sparkles size={14} className="text-amber-500" />
                      Up to 4 Members
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-3 text-lg"
                >
                  {loading ? 'Creating...' : 'Create Family'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleJoinFamily} className="space-y-6">
                {/* Show invite details if available */}
                {inviteDetails && (
                  <div className="bg-emerald-50 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="text-emerald-600 flex-shrink-0" size={24} />
                    <div>
                      <p className="font-semibold text-emerald-800">
                        You're invited to join "{inviteDetails.family_name}"
                      </p>
                      <p className="text-sm text-emerald-600">
                        Invited by {inviteDetails.invited_by_name} as {inviteDetails.role}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invite Code
                  </label>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="Enter 8-character code"
                    className="input-field w-full text-lg text-center tracking-widest font-mono"
                    maxLength={8}
                    autoFocus={!inviteDetails}
                    readOnly={!!inviteDetails}
                  />
                  {!inviteDetails && (
                    <p className="text-xs text-gray-500 mt-2">
                      Ask the family admin for the invite code
                    </p>
                  )}
                </div>

                {/* Security Note */}
                <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4">
                  <Shield size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Secure Access</p>
                    <p className="text-xs text-blue-600 mt-1">
                      You'll be able to view the family's portfolio data. Only admins can make changes.
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || inviteCode.length < 8}
                  className="btn-primary w-full py-3 text-lg"
                >
                  {loading ? 'Joining...' : (inviteDetails ? `Join ${inviteDetails.family_name}` : 'Join Family')}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          🔒 Your data is encrypted and never shared
        </p>
      </div>
    </div>
  );
};

export default FamilySetup;
